import React, { Fragment, useState } from 'react';
import Select, { SelectProps } from 'react-dropdown-select';
import isEmpty from 'lodash/isEmpty';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { Icon } from '../Icon';
import { Text } from '../Typography';
import Option from './Option';

interface ISelectProps extends SelectProps<any> {
  dataCy?: string;
  id?: string;
  inputHeight?: number;
  selectId?: string;
  error?: boolean;
  sections?: { title: string; type: string }[];
}

const Layout = styled.div`
  width: 100%;

  > * {
    width: 100%;
  }
`;

const INPUT_HEIGHT = 44;
const ITEM_HEIGHT = 44;
const MAX_ITEMS = 5.5;

const StyledSelect = styled(Select)<ISelectProps & { $inputHeight: number; $isOpen?: boolean; $error?: boolean }>`
  width: 100%;
  max-width: 100%;
  height: ${({ $inputHeight }) => toRem($inputHeight)};
  margin: 0 !important;
  padding: 0 ${toRem(11)} !important;
  border-radius: ${toRem(5.5)}!important;
  border: ${({ $error }) => `${toRem(1)} solid ${!$error ? theme.color.neutral[200] : theme.color.danger.medium}`} !important;
  background-color: ${theme.color.white};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(13.25)};
  line-height: 100%;
  color: ${theme.color.neutral[900]};

  &:hover {
    background-color: ${theme.color.white};
    border-color: ${({ $error }) => (!$error ? theme.color.neutral[400] : theme.color.danger.medium)} !important;
  }

  &:focus-within {
    background-color: ${theme.color.white}!important;
    border-color: ${({ $error }) => (!$error ? theme.color.neutral[400] : theme.color.danger.medium)} !important;
    box-shadow: none !important;
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 1 !important;
      background: ${theme.color.neutral[100]} !important;
      border: 1px solid ${theme.color.neutral[200]} !important;

      .drop-handle {
        display: none;
      }
    `}

  .react-dropdown-select {
    width: 100%;
    height: ${({ $inputHeight }) => toRem($inputHeight)};
    margin: 0;
    padding: 0;

    &-input {
      display: flex;
      flex-grow: 1;
      width: auto;
      margin-left: 0;
      font-family: ${theme.font.family.euclid};
      font-weight: ${theme.font.weight.regular};
      font-size: ${toRem(13.25)};
      line-height: 100%;
      color: ${theme.color.neutral[900]};
      overflow: visible;

      ${({ searchable, values }) =>
        !searchable &&
        values.length > 0 &&
        css`
          visibility: hidden;
          width: ${toRem(40)};
        `}

      &::placeholder {
        color: ${theme.color.neutral[400]};
      }

      &:read-only {
        margin-left: 0;
      }
    }

    &-content {
      position: absolute;
      left: 0;
      flex-flow: row nowrap;
      justify-content: flex-start;
      align-items: center;
      width: 100%;
      height: 100%;
      padding-left: ${toRem(11)};
      overflow: hidden;

      input {
        max-width: 90%;
      }

      span {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        width: auto;
        height: 100%;
        max-height: ${toRem(27.5)};
      }
    }

    &-type-single {
      span {
        position: absolute;
      }
    }

    &-type-multi {
      overflow: auto;
      -ms-overflow-style: none;
      scrollbar-width: none;
      padding-left: unset;
      position: unset;
      left: unset;

      input {
        max-width: unset;
        width: ${toRem(20)};
      }
    }

    &-option {
      display: flex;
      flex-grow: 0;
      flex-shrink: 0;
      flex-flow: row nowrap;
      justify-content: flex-start;
      align-items: center;
      width: auto;
      height: ${toRem(27.5)};
      margin: 0 ${toRem(5.5)} 0 0;
      padding: 0 ${toRem(13.75)};
      border-radius: ${toRem(5.5)};
      background-color: ${theme.color.neutral[100]};
      color: ${theme.color.neutral[900]};
      cursor: auto;

      &-label {
        font: inherit;
        color: inherit;
      }

      &-remove {
        padding: 0 0 0 ${toRem(5.5)};
        color: ${theme.color.neutral[800]};
        cursor: pointer;

        &:hover {
          color: ${theme.color.danger.medium} !important;
        }
      }

      &:hover {
        background-color: #dee0e5;
        color: ${theme.color.neutral[900]};
      }
    }

    &-no-data {
      display: flex;
      flex-flow: row nowrap;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: ${toRem(ITEM_HEIGHT)};
      padding: ${toRem(5.5)};
      background-color: ${theme.color.white};
      color: ${theme.color.neutral[800]};
    }

    &-dropdown {
      top: ${toRem(INPUT_HEIGHT + 3)};
      left: 0;
      right: 0;
      width: 100%;
      height: auto;
      max-height: ${toRem(ITEM_HEIGHT * MAX_ITEMS)};
      border-radius: ${toRem(5.5)};
      border: ${toRem(1)} solid ${theme.color.neutral[200]};
      box-shadow: ${theme.shadow.dialog};
    }
  }

  ${({ searchable, $isOpen }) =>
    searchable &&
    $isOpen &&
    css`
      .react-dropdown-select-content {
        span {
          display: none !important;
        }
      }
    `}
`;

interface DropdownHandleProps {
  isOpen?: boolean;
}

const DropdownHandle = styled.div.attrs({ className: 'drop-handle' })<DropdownHandleProps>`
  position: absolute;
  top: 0;
  right: 0;
  justify-content: center;
  align-items: center;
  width: ${toRem(38.5)};
  height: 100%;
  border-radius: ${toRem(5.5)};
  background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 15%);

  svg {
    fill: ${theme.color.neutral[800]};
    transition: transform ${theme.transition.icon};
  }

  &:hover {
    path {
      stroke: none !important;
    }
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `}
`;

const Title = styled(Text)`
  color: ${theme.color.neutral[500]};
  margin: ${toRem(8)} ${toRem(16.5)};
  cursor: auto;
`;

const NoData = styled.div`
  display: flex;
  justify-content: center;
  padding: ${toRem(12)} ${toRem(13.75)};
  color: ${theme.color.neutral[800]};
  cursor: auto;
`;

interface IOption {
  value: any;
  label: string;
}

const SelectDropdown: React.FC<ISelectProps> = ({
  backspaceDelete,
  className,
  create,
  createNewLabel,
  dataCy,
  disabled,
  dropdownHandleRenderer,
  dropdownRenderer,
  handleKeyDownFn,
  id,
  inputRenderer,
  itemRenderer,
  contentRenderer,
  multi,
  onChange,
  onCreateNew,
  options,
  sections,
  placeholder,
  searchable = false,
  values,
  inputHeight = INPUT_HEIGHT,
  selectId,
  error,
  valueField,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const onDropdownChange = () => setIsOpen((open) => !open);

  // https://github.com/sanusart/react-dropdown-select/blob/master/docs/src/examples/ItemRenderer.js
  const customItemRenderer = ({ item, methods }: any) =>
    item.disabled ? (
      <Option key={item.label} disabled aria-label={`Option: ${item.label} is not available to choose.`}>
        <div>{item.label}</div>
      </Option>
    ) : (
      <Option key={item.label} onClick={() => methods.addItem(item)} className={methods.isSelected(item) ? 'active' : ''}>
        <div>{item.label}</div>
      </Option>
    );

  // https://github.com/sanusart/react-dropdown-select/blob/master/docs/src/examples/CustomDropdownHandle.js
  const customDropdownHandleRenderer = ({ state }: any) => (
    <DropdownHandle isOpen={state.dropdown}>
      <Icon icon="Dropdown" width={16.5} height={16.5} />
    </DropdownHandle>
  );

  const sectionsDropdownRenderer = ({ props, state, methods }: any) => {
    const regexp = new RegExp(state.search, 'i');
    const searchOptions: IOption[] = state.search ? props.options.filter((item: IOption) => regexp.test(item.label)) : props.options;

    if (searchOptions.length === 0) {
      return <NoData>No data</NoData>;
    }
    return (
      <>
        {sections?.map((section) => {
          const options = searchOptions.filter((item: IOption) => item.value.type === section.type);

          return (
            options.length > 0 && (
              <Fragment key={section.type}>
                <Title size="xsmall" fontWeight="semibold">
                  {section.title}
                </Title>
                {options.map((option) => {
                  if (!props.keepSelectedInList && methods.isSelected(option)) {
                    return null;
                  }
                  return customItemRenderer({ item: option, methods });
                })}
              </Fragment>
            )
          );
        })}
      </>
    );
  };

  // Check if label is empty ('') to send and empty array [] as values. Placeholder requires this to display.
  const noSelectedValues = values.filter((selected) => isEmpty(selected?.label)).length >= 1;

  return (
    <Layout className={className} data-cy={dataCy} id={id} {...rest}>
      <StyledSelect
        id={selectId}
        backspaceDelete={backspaceDelete}
        create={create}
        createNewLabel={createNewLabel}
        disabled={disabled}
        dropdownHandleRenderer={dropdownHandleRenderer || customDropdownHandleRenderer}
        dropdownRenderer={sections ? sectionsDropdownRenderer : dropdownRenderer}
        handleKeyDownFn={handleKeyDownFn}
        inputRenderer={inputRenderer}
        itemRenderer={itemRenderer || customItemRenderer}
        contentRenderer={contentRenderer}
        multi={multi}
        onChange={onChange}
        onCreateNew={onCreateNew}
        onDropdownClose={onDropdownChange}
        onDropdownOpen={onDropdownChange}
        options={options}
        placeholder={placeholder}
        searchable={searchable}
        values={noSelectedValues ? [] : values}
        valueField={valueField}
        $inputHeight={inputHeight}
        $isOpen={isOpen}
        $error={error}
      />
    </Layout>
  );
};

export default SelectDropdown;
